import {
  ProAlertCreateRequestItem,
  ProAlertCreateResponse,
} from '../models/pro.model';
import { chunkArray, runWithConcurrency } from '../utils/batchHelpers';
import { createProAlert } from './pro-alert';

/**
 * Creates alerts for vessels in batches, with progress callback for user feedback.
 *
 * @param vesselsWithEmails - Array of vessels with emails to alert (input vessels).
 * @param allCreatedVessels - Array of all created vessel records (from all batches).
 * @param batchSize - Optional batch size for alert creation (default: 50).
 * @param concurrency - Optional concurrency limit for alert batch creation (default: 2).
 * @param onProgress - Optional callback for progress updates: (completed, total, failed) => void
 * @returns {Promise<ProAlertCreateResponse | null>} Aggregated alert responses or null if no alerts needed.
 */

interface CreateAlertsForVesselsOptions {
  vesselsWithEmails: { imo: number; emails: string[] }[];
  allCreatedVessels: { vessel_id: string; imo: string }[];
  batchSize?: number;
  concurrency?: number;
  setProgress?: (progress: {
    completed: number;
    total: number;
    failed: number;
  }) => void;
  setResponse?: (response: ProAlertCreateResponse | null) => void;
}

const createAlertsForVessels = async ({
  vesselsWithEmails,
  allCreatedVessels,
  batchSize = 5,
  concurrency = 2,
  setProgress,
  setResponse,
}: CreateAlertsForVesselsOptions): Promise<ProAlertCreateResponse | null> => {
  if (!vesselsWithEmails.length) return null;

  // For each vessel with emails, find the corresponding vessel_id from the created vessels
  const vesselsWithIds = vesselsWithEmails.map((vessel) => {
    const vesselObj = allCreatedVessels.find(
      (nv) => nv.imo === String(vessel.imo)
    );
    return vesselObj
      ? { ...vessel, vessel_id: vesselObj.vessel_id }
      : { ...vessel, vessel_id: '' };
  });

  // Prepare the alert creation payload, only including vessels with a valid vessel_id
  const proAlertItems: ProAlertCreateRequestItem[] = vesselsWithIds
    .filter((vessel) => vessel.vessel_id)
    .map((vessel) => ({
      vessel_id: vessel.vessel_id,
      emails: vessel.emails,
    }));

  if (!proAlertItems.length) return null;

  // Batch the alert creation requests if needed
  const alertBatches = chunkArray(proAlertItems, batchSize);
  let alertsCompleted = 0;
  let alertsFailed = 0;
  const totalAlerts = proAlertItems.length;
  let failedBatches = 0;
  const failedDetails: any[] = [];
  const tasks = alertBatches.map((batch) => async () => {
    try {
      const resp = await createProAlert({ alerts: batch });
      alertsCompleted += batch.length;
      if (setProgress)
        setProgress({
          completed: alertsCompleted,
          total: totalAlerts,
          failed: alertsFailed,
        });
      if (setResponse) setResponse(resp);
      return resp;
    } catch (e: any) {
      alertsFailed += batch.length;
      failedBatches += 1;
      failedDetails.push(
        e?.response?.data || { error: e?.message || 'Unknown error' }
      );
      if (setProgress)
        setProgress({
          completed: alertsCompleted,
          total: totalAlerts,
          failed: alertsFailed,
        });
      if (setResponse) setResponse(null);
      return null;
    }
  });
  const responses: (ProAlertCreateResponse | null)[] = await runWithConcurrency(
    tasks,
    concurrency
  );
  const allAlerts = responses.flatMap((res) =>
    res && res.alerts ? res.alerts : []
  );
  if (allAlerts.length === 0) {
    const result = {
      message: `No PRO alerts were created.`,
      alerts: [],
    };
    if (setResponse) setResponse(result);
    return result;
  }
  let msg = `${allAlerts.length} PRO alerts created.`;
  if (failedBatches > 0) {
    msg += 'Some alerts failed to be created.';
  }
  const result = {
    message: msg,
    alerts: allAlerts,
  };
  if (setResponse) setResponse(result);
  return result;
};

export default createAlertsForVessels;
